import React from 'react';

import { debounce } from 'lodash';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { ExportAnnotationsPurpose, exportAnnotations } from 'src/api/projects';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import { Modality } from 'src/types/api/data/image';

interface ExportAnnotationsButtonProps {
  projectId: string;
  modalityLabel: Modality;
}

const ExportAnnotationsButton = ({
  projectId,
  modalityLabel,
}: ExportAnnotationsButtonProps): JSX.Element => {
  const { openAlertSnackbar } = useAlertSnackbar();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openExportAnnotationsMenu = Boolean(anchorEl);
  const [isDisabled, setIsDisabled] = React.useState(false);

  const handleOpenExportAnnotationsMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseExportAnnotationsMenu = () => {
    setAnchorEl(null);
  };

  // delay enabling export buttons to prevent multiple API calls
  const enableExportAnnotations = debounce(() => setIsDisabled(false), 300);

  const handleExportAnnotations = async (purpose: ExportAnnotationsPurpose) => {
    setIsDisabled(true);
    openAlertSnackbar({
      description: `Exporting data for ${purpose}. Please wait to download the file.`,
      severity: 'info',
    });

    try {
      await exportAnnotations(projectId, modalityLabel, purpose);
      openAlertSnackbar({
        severity: 'success',
        description: `Successfully exported data for ${purpose}.`,
      });
    } catch {
      openAlertSnackbar({
        severity: 'error',
        description: `Failed to export annotations for ${purpose}.`,
      });
    } finally {
      handleCloseExportAnnotationsMenu();
      enableExportAnnotations();
    }
  };

  const exportPurposes: ExportAnnotationsPurpose[] = [
    'INVOICING',
    'TRAINING',
    'QC',
  ];

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        sx={{
          textTransform: 'uppercase',
          fontSize: '12px',
          fontWeight: 500,
          borderRadius: '8px',
          color: '#ffffff',
        }}
        id="basic-button"
        aria-controls={openExportAnnotationsMenu ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openExportAnnotationsMenu ? 'true' : undefined}
        onClick={handleOpenExportAnnotationsMenu}
        disabled={isDisabled}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />}
      >
        {isDisabled ? 'Loading...' : 'Export Annotations'}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openExportAnnotationsMenu}
        onClose={handleCloseExportAnnotationsMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {exportPurposes.map(purpose => (
          <MenuItem
            key={purpose}
            onClick={() => handleExportAnnotations(purpose)}
            disabled={isDisabled}
          >
            FOR {purpose}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ExportAnnotationsButton;
